/**
 * Karte „Wer fragt hier an?" — EINE reine Regel: welche Angabe erscheint, mit welcher Quelle und
 * welchem Stand, und wann die Karte ueberhaupt entfaellt.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 2.2 (drei Verdichtungsstufen, Regel „Quelle +
 * Stand an JEDER Angabe"), § 2.3 (Soll-Tabelle der Karte im Anfragen-Drawer), § 4.2 (ein Zustand,
 * ein Satz). Kriterium R0.9 in docs/LOOP-RECHERCHE.md.
 *
 * ── Stufe 1 des tieferen Firmenprofils (07.08.2026, von Antoine freigegeben) ──────────────────
 * Zwei Erweiterungen, beide OHNE neue Datenquelle und ohne einen einzigen zusaetzlichen Abruf:
 *
 *  1. **Die Google-Bewertung erscheint hier.** Sie liegt seit Migration 93 in jedem geglueckten
 *     Places-Abruf (`rating`, `user_ratings_total`) und stand bisher nur im Kunden-Profil. Das ist
 *     ein ANSCHLUSS, kein Abruf — die Route liest ausschliesslich die eigene Datenbank
 *     (Fetch-Rekorder in `apps/api/test/firmenprofil-anfrage.test.ts`). Form: „4,2 von 5 bei 38
 *     Bewertungen" — Tatsache mit Quelle und Stand, nie eine Einschaetzung (§ 6.1).
 *  2. **„Gesucht und nichts gefunden" wird von „nicht nachgesehen" unterschieden**, und zwar je
 *     Quelle: Handelsregister, Google Places, Impressum (R3.1), USt-IdNr.-Bestaetigung (R3.2).
 *     Die Zustaende dafuer werden nicht erfunden — sie kommen aus den bereits abgenommenen Regeln.
 *
 * Ausdruecklich NICHT dabei: ein Score, eine Ampel, eine abgeleitete Note. Einen Bonitaetsscore
 * will Antoine spaeter KAUFEN (Stufe 3, eigener Vorgang); ein selbst erzeugtes Urteil ist
 * verboten (Konzept § 6.1, Entscheidung P-4).
 *
 * ── Warum das eine reine Funktion ist und kein JSX ────────────────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (kein `test`-Skript, kein vitest, keine `*.test.tsx` — im
 * Beweis zu R0.2 festgehalten, in R0.7 erneut). Eine Regel wie „eine Angabe ohne Datum wird nicht
 * als datiert ausgegeben" waere in einer React-Komponente unpruefbar: genau die Stelle, an der
 * eine Anzeige leise wieder anfaengt, einen Stand zu behaupten, den niemand kennt. Deshalb steht
 * hier die ganze Entscheidung und in der Komponente nur noch `.map()`.
 *
 * Die Tests liegen in `apps/api/test/firmenprofil-karte.test.ts` — dieselbe Ueberlegung wie bei
 * `@maestro/anreicherung` (R0.7) und der Deckungsregel (§ 5.9 / PR #419): dort laeuft die Suite in
 * der CI garantiert mit. Dieses Paket hat deshalb bewusst KEIN `test`-Skript (die Ratsche
 * `apps/api/test/ci-testabdeckung.test.ts` verlangt fuer jedes Paket MIT `test`-Skript einen
 * eigenen CI-Aufruf — ein zusaetzliches Ziel ohne zusaetzliche Sicherheit).
 *
 * ── Warum ein eigenes Paket und nicht `@maestro/anreicherung` ─────────────────────────────────
 * `@maestro/anreicherung` beantwortet EINE Frage: „In welchem Zustand ist die Recherche, welcher
 * eine Satz gehoert dazu, welche eine Handlung gibt es?" R0.7 prueft die Vollstaendigkeit dieser
 * Liste gegen `toHaveLength(9)`. Die Frage hier ist eine andere: „Welche FAKTEN zeigt die Karte,
 * woher stammen sie und von wann sind sie?" Beides in eine Datei zu legen, wuerde die abgenommene
 * Zustandsliste mit einer Feldauswahl vermischen. Dieses Paket RUFT die Abbildung auf — es baut
 * keinen zweiten Zustandsbaum (dieselbe Regel, die R0.8 serverseitig befolgt).
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, keine implizite Zeitzone: `jetzt` und `zeitzone` sind
 * Parameter. Ohne `zeitzone` formatiert `Intl` in der Zone der Laufzeit — im Browser also in der
 * des Betrachters (Vorgabe aus R0.7), im Test in der uebergebenen Zone (deterministisch).
 */
import {
  anreicherungsAnzeige,
  trefferauswahlSatz,
  type AnreicherungsHandlung,
  type AnreicherungsZeile,
  type AnreicherungsZustand,
  type TrefferEintrag,
  type Trefferliste,
} from "@maestro/anreicherung";
import { impressumKarte, QUELLE_IMPRESSUM, type ImpressumZeile } from "@maestro/impressum-anzeige";
import { standDatum, standZeile, type RechercheQuelle } from "@maestro/recherche-stand";
import { ustIdKarte, QUELLE_USTID, type UstIdZeile } from "@maestro/ustid-anzeige";

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/** Ein Handelsregister-Treffer, so wie die API ihn ausliefert (`matches[]`). */
export interface FirmenprofilTreffer {
  companyName?: string | null;
  legalForm?: string | null;
  registerNumber?: string | null;
  registerCourt?: string | null;
  seat?: string | null;
  representatives?: string[] | null;
  lastEntryDate?: string | null;
}

/** Die Lookup-Zeile aus der Antwort — Lebenszyklus-Felder wie in `@maestro/anreicherung`. */
export interface FirmenprofilZeile extends AnreicherungsZeile {
  matches?: FirmenprofilTreffer[] | null;
}

/* ── Stufe 1: die drei uebrigen Quellen, jede mit ihrem Modulschalter ───────────────────────── */

/**
 * Die Zeile aus `google_places_lookups`, soweit die Karte sie braucht.
 *
 * Bewertung und Anzahl liegen dort seit Migration 93 in JEDEM geglueckten Abruf (`rating`,
 * `user_ratings_total`) — sie werden hier nur ANGESCHLOSSEN. Es entsteht kein zusaetzlicher
 * Google-Aufruf; die Karte liest ausschliesslich, was bereits gespeichert ist (mit einem
 * Fetch-Rekorder gemessen, `apps/api/test/firmenprofil-anfrage.test.ts`).
 */
export interface FirmenprofilPlacesZeile extends AnreicherungsZeile {
  /** Googles Durchschnittsnote (0–5). */
  rating?: number | null;
  /** Anzahl der Bewertungen, auf denen sie beruht. */
  userRatingsTotal?: number | null;
}

/** Ein Quellen-Block der Antwort: der Modulschalter dieser Quelle plus ihre Zeile (oder keine). */
export interface FirmenprofilPlacesBlock {
  /** `tenantSettings.googlePlacesEnabled`. */
  modulAktiv: boolean;
  /** `null` heisst: zu dieser Firma existiert keine Places-Zeile — also wurde nie nachgesehen. */
  zeile: FirmenprofilPlacesZeile | null;
}

export interface FirmenprofilImpressumBlock {
  /** `tenantSettings.impressumEnabled`. */
  modulAktiv: boolean;
  /** Die hinterlegte Website der Firma — ohne sie gibt es kein Impressum nachzuschlagen. */
  website: string | null;
  zeile: ImpressumZeile | null;
}

export interface FirmenprofilUstIdBlock {
  /** `tenantSettings.ustidEnabled`. */
  modulAktiv: boolean;
  /**
   * Ist die EIGENE USt-IdNr. des Betriebs hinterlegt? Bewusst nur ja/nein und nicht die Nummer:
   * gebraucht wird hier ausschliesslich, OB ueberhaupt gefragt werden kann (eine Bestaetigung gilt
   * fuer den Anfragenden, GATE-D). Die Nummer selbst hat in einer Anfragen-Karte nichts zu suchen.
   */
  eigeneUstIdNrHinterlegt: boolean;
  /** Die USt-IdNr. der Kundenfirma, soweit bekannt (`lib/firmenprofil.ts`, R3.2). */
  ustIdNr: string | null;
  zeile: UstIdZeile | null;
}

/**
 * `data` von `GET /api/inquiries/:id/firmenprofil` (R0.8).
 *
 * `zustand` kommt vom Server mit, wird hier aber NICHT verwendet: der Satz zum Zustand traegt
 * Uhrzeiten, die in die Zone des Betrachters gehoeren — deshalb rechnet die Oberflaeche ihn mit
 * derselben Abbildung selbst (so hat R0.8 es ausdruecklich vorgesehen). Das Feld bleibt im Typ,
 * damit der Test beide Wege gegeneinander halten kann (Anti-Drift-Gegenprobe).
 *
 * ── Warum die drei neuen Bloecke OPTIONAL sind (und was `undefined` bedeutet) ────────────────
 * `undefined` heisst: diese Antwort traegt zu der Quelle ueberhaupt keine Aussage — dann entsteht
 * fuer sie auch KEINE Zeile in der Karte. Das ist der Unterschied, um den es in Stufe 1 geht: aus
 * einem fehlenden Feld „nicht nachgesehen" zu folgern waere selbst wieder eine Behauptung. Erst
 * ein gelieferter Block mit `zeile: null` sagt: es gibt keine Zeile, also wurde nie nachgesehen.
 */
export interface FirmenprofilAntwort {
  inquiryId?: string;
  company: string | null;
  modulAktiv: boolean;
  zustand?: string;
  handelsregister: FirmenprofilZeile | null;
  letzteVeroeffentlichung: { title: string; publishedAt: string | null } | null;
  bundesanzeigerSuchUrl: string | null;
  /** Stufe 1 — Google Places (Bewertung + Zustand). */
  places?: FirmenprofilPlacesBlock | null;
  /** Stufe 1 — Impressum (nur Zustand; die Felder zeigt heute das Kunden-Profil). */
  impressum?: FirmenprofilImpressumBlock | null;
  /** Stufe 1 — USt-IdNr.-Bestaetigung (nur Zustand; den Nachweis zeigt das Kunden-Profil). */
  ustid?: FirmenprofilUstIdBlock | null;
}

export interface FirmenprofilKarteLage {
  /** Die geladene Antwort — `null` heisst: noch nichts da (Ladezustand, andere Anfrage). */
  profil: FirmenprofilAntwort | null;
  /**
   * Eingang der Anfrage. Er ist der STAND der Angabe „Firma": die kommt nicht aus einer
   * Recherche, sondern aus dem, was der Anfragende selbst geschrieben hat. Ohne diesen Wert
   * duerfte die Firma nicht als datierte Angabe erscheinen (harte Regel dieses Loops).
   */
  anfrageEingang: string | null;
  /** Bezugszeitpunkt. Parameter, damit die Regel rein und testbar bleibt. */
  jetzt: Date;
  /** IANA-Zone. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
  /** Anzeigename der Modul-Kachel — muss zu `apps/web/src/pages/settings.tsx` passen. */
  modulName?: string;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/**
 * Die Quellen, die diese Karte kennt. Mehr gibt es heute nicht — und keine ohne Namen.
 * Eine Teilmenge von `RECHERCHE_QUELLEN` (R1.6): die Namen werden nicht zweimal geschrieben,
 * sonst hiesse dieselbe Quelle je nach Flaeche anders.
 */
export type FirmenprofilQuelle = Extract<
  RechercheQuelle,
  "Anfrage" | "Handelsregister" | "Bundesanzeiger" | "Google Places" | "Impressum" | "USt-IdNr.-Bestätigung" | "Auskunftei"
>;

export interface FirmenprofilAngabe {
  /** Feldname, z.B. „Rechtsform". */
  label: string;
  wert: string;
  quelle: FirmenprofilQuelle;
  /** Formatierter Stand („12.06.2026") — `null`, wenn die Quelle KEIN Datum geliefert hat. */
  stand: string | null;
  /**
   * Die fertige Herkunftszeile, die unter der Angabe steht: „Handelsregister · Stand 12.06.2026"
   * bzw. „Bundesanzeiger · ohne Datum". Bewusst hier zusammengesetzt und nicht in der Komponente:
   * „ohne Datum" ist eine Aussage, keine Formatierung — sie muss gepruefte Regel sein.
   */
  herkunft: string;
}

/**
 * Was zu EINER Quelle zu sagen ist — der Kern von Stufe 1.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  „GESUCHT UND NICHTS GEFUNDEN" IST EINE ANDERE AUSSAGE ALS „NICHT NACHGESEHEN"
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * Bis hierher stand bei einer fehlenden Angabe schlicht nichts, und man sah der Karte nicht an, ob
 * ueberhaupt gesucht wurde. Beide Faelle sahen gleich aus — obwohl der eine ein Rechercheergebnis
 * ist („diese Firma hat keinen Google-Eintrag") und der andere gar keins.
 *
 * Die Zustaende dafuer werden hier NICHT erfunden: sie kommen aus `@maestro/anreicherung` (R0.7,
 * neun Zustaende, gegen `toHaveLength(9)` abgenommen) bzw. — fuer die beiden Quellen mit eigenen
 * Vorbedingungen — aus `@maestro/impressum-anzeige` (R3.1) und `@maestro/ustid-anzeige` (R3.2).
 * Dieses Paket bringt nur die Sichtbarkeit: je Quelle EINE Zeile, an derselben Stelle, in
 * derselben Form.
 *
 * ── Warum es hier keine Schaltflaeche gibt ───────────────────────────────────────────────────
 * `@maestro/anreicherung` nennt fuer vier Zustaende die Handlung `neu_abrufen`. Die fuehrt ueber
 * `POST /api/anreicherung/handelsregister` und stoesst genau EINE Quelle an: das Handelsregister.
 * Ein gleich aussehender Knopf an der Places-, Impressum- oder USt-IdNr.-Zeile wuerde entweder
 * nichts tun oder etwas anderes tun, als er verspricht — beides ist derselbe Fehler, den R0.7
 * bewusst vermieden und R0.13 erst dann geheilt hat, als es einen echten Weg gab. Diese Zeilen
 * sind deshalb reine Aussagen. Die eine Handlung der Karte (Handelsregister) bleibt, wo sie war.
 */
export interface FirmenprofilQuellenStand {
  /** Der Quellenname — derselbe wie in jeder Herkunftszeile (`RECHERCHE_QUELLEN`, R1.6). */
  quelle: FirmenprofilQuelle;
  zustand: AnreicherungsZustand;
  /**
   * Die Kopfzeile: sobald die Quelle uns geantwortet hat, ist es die volle Herkunftszeile
   * („Google Places · Stand 12.06.2026") — sie ist der Beleg dafuer, DASS nachgesehen wurde, und
   * seit wann das Ergebnis steht. Wurde nie nachgesehen, steht dort nur der Quellenname: ein
   * Stand, den es nicht gibt, wird nicht behauptet (R1.6).
   */
  kopf: string;
  /** Der EINE Satz zum Zustand — `null` nur bei `aktuell`, wo die Angaben selbst sprechen. */
  satz: string | null;
  /**
   * Wurde bei dieser Quelle tatsaechlich nachgesehen?
   *
   * `true` genau dann, wenn eine Zeile existiert UND entweder ein Ergebnis vorliegt
   * (`found`/`not_found`/`ambiguous`) ODER ein Abrufzeitpunkt eingetragen ist.
   *
   *  - `not_found` ist ausdruecklich `true`: **„gesucht, nichts gefunden" IST ein Ergebnis.** Genau
   *    diese Unterscheidung ist der Zweck des Feldes.
   *  - Ein Ergebnis OHNE `looked_up_at` (Bestandszeile) zaehlt trotzdem als nachgesehen — die
   *    Herkunftszeile sagt dann ehrlich „ohne Datum" (R1.6), statt eins zu erfinden.
   *  - `error`/`skipped`/`pending` sind `false`, SOLANGE kein Abrufzeitpunkt eingetragen ist: dann
   *    ist keine Auskunft angekommen, und der Satz sagt, warum. Steht dort ein Zeitpunkt, wurde
   *    tatsaechlich gefragt — dann ist `true` die Wahrheit, auch wenn nichts zurueckkam.
   */
  nachgesehen: boolean;
}

export interface FirmenprofilKarte {
  /** `false` = die Karte wird gar nicht gerendert (keine Firma an der Anfrage, nichts geladen). */
  sichtbar: boolean;
  /** Ueberschrift der Karte. */
  titel: string;
  /** Der Firmenname — steht im Kopf, auch eingeklappt. */
  firma: string;
  /**
   * Kurzinfo im Kopf, damit EINGEKLAPPT sichtbar bleibt, ob es ueberhaupt etwas gibt (P-1):
   * entweder ein Zaehler mit Stand („2 Angaben · Stand 12.06.2026") oder der eine Zustandssatz.
   */
  kurzinfo: string;
  zustand: AnreicherungsZustand;
  /** Der EINE Satz zum Zustand aus `@maestro/anreicherung` — `null` nur bei `aktuell`. */
  satz: string | null;
  /** Die EINE moegliche Handlung — `null`, wenn es fuer diesen Zustand keinen Weg gibt. */
  handlung: AnreicherungsHandlung | null;
  /** Jede Angabe traegt Quelle + Stand. Eine Angabe ohne beides entsteht hier nicht. */
  angaben: FirmenprofilAngabe[];
  /**
   * Je Quelle eine Zeile: wurde nachgesehen — und was kam dabei heraus? Nur Quellen, zu denen die
   * Antwort ueberhaupt etwas sagt (siehe `FirmenprofilAntwort`). Das Handelsregister ist immer
   * dabei, die drei uebrigen, sobald ihr Block geliefert wird.
   */
  quellen: FirmenprofilQuellenStand[];
  /**
   * Die Werte aus Google Places, die diese Karte GERADE ANZEIGT — nicht mehr und nicht weniger.
   * Sie sind die Eingabe fuer `<GoogleAttribution />` (R4.5, § 6.2): die Attributionspflicht
   * haengt an den Daten selbst, nicht an einem Schalter. Steht keine Google-Angabe auf dem Schirm,
   * ist die Liste leer und es gibt nichts zu attribuieren.
   */
  googleWerte: readonly unknown[];
  /** Deeplink auf die Bundesanzeiger-Ergebnisliste (nie ein Einzeldokument). */
  bundesanzeigerSuchUrl: string | null;
}

/* ────────────────────────────── Formatierung ───────────────────────────────────────────────── */

/** Anzeigename der Modul-Kachel (`apps/web/src/pages/settings.tsx`, `apps/api/lib/firmenprofil.ts`). */
export const MODUL_HANDELSREGISTER = "Handelsregister-Check";

/** Anzeigename der Places-Kachel — exakt wie in `apps/web/src/pages/settings.tsx` (MODULE-Liste). */
export const MODUL_PLACES = "Google-Places-Lookup";

/** Ueberschrift der Karte — der Wortlaut aus Konzept § 2.3. */
export const KARTEN_TITEL = "Wer fragt hier an?";

/** Beschriftung der Bewertungs-Angabe. Die Quelle steht in ihrer Herkunftszeile, nicht im Label. */
export const LABEL_BEWERTUNG = "Bewertung";

/**
 * Der Platzhalter fuer „die eigene USt-IdNr. ist hinterlegt".
 *
 * `ustIdKarte()` fragt diesen Wert ausschliesslich auf Vorhandensein ab (`(…).trim()` im Zweig
 * „nicht eingerichtet"). Die Anfragen-Karte bekommt vom Server deshalb nur ein ja/nein und setzt
 * hier einen sprechenden Platzhalter ein — die Nummer selbst wird nirgends uebertragen und
 * nirgends angezeigt. Genau dasselbe Vorgehen wie im Kunden-Profil (R3.2,
 * `apps/web/src/pages/customer-detail.tsx`: `eigeneVorhanden ? "vorhanden" : null`).
 */
const EIGENE_USTID_VORHANDEN = "vorhanden";

/**
 * Datum und Herkunftszeile kommen seit R1.6 aus `@maestro/recherche-stand` — die private Kopie
 * dieser beiden Funktionen ist dort aufgegangen. Grund: dieselbe Zeile steht inzwischen auch im
 * Kunden-Profil, an der Places-Karte und an der Wetter-Karte. Zwei Fassungen einer Regel bedeuten
 * zwei Meinungen darueber, was ohne Datum passiert — und genau dort entsteht das Falschdatum.
 */
function angabe(
  label: string,
  wert: string | null | undefined,
  quelle: FirmenprofilQuelle,
  standIso: string | null | undefined,
  zeitzone?: string,
): FirmenprofilAngabe | null {
  const text = typeof wert === "string" ? wert.trim() : "";
  if (!text) return null;
  const stand = standDatum(standIso, zeitzone);
  return { label, wert: text, quelle, stand, herkunft: standZeile(quelle, standIso, zeitzone) };
}

/**
 * Die Google-Bewertung als SATZ AUS TATSACHEN: „4,2 von 5 bei 38 Bewertungen".
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ANGABE, NIE URTEIL (Antoines Grenze, 07.08.2026)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * „4,2 von 5 bei 38 Bewertungen" ist eine Angabe. „Wirkt seriös" waere ein Urteil. Deshalb
 * entsteht hier ausschliesslich eine Zahl mit ihrem Bezug — keine Ampel, keine Einordnung, keine
 * abgeleitete Note, keine Schwelle („ab 4,0 gut"). Wer eine Bewertung des Kunden will, kauft einen
 * Score bei einer Auskunftei; MAESTRO ist keine (Konzept § 6.1, Entscheidung P-4).
 *
 * `null` heisst: es gibt keine Bewertung — dann steht dort nichts, und nichts wird ersetzt.
 * Ohne Anzahl bleibt es bei der Note allein: eine erfundene oder weggelassene Bezugsgroesse waere
 * genau die Suggestion, die der Loop abschafft (dieselbe Ueberlegung wie „2 von 3" statt „67 %"
 * in `lib/kundenhistorie.ts`).
 *
 * Zahlen deutsch: „4,2" mit Komma, „1.284" mit Punkt — ueber `Intl`, nicht von Hand.
 */
export function bewertungsAngabe(zeile: FirmenprofilPlacesZeile | null | undefined): string | null {
  const note = zeile?.rating;
  if (typeof note !== "number" || !Number.isFinite(note)) return null;
  const gerundet = note.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const anzahl = zeile?.userRatingsTotal;
  if (typeof anzahl !== "number" || !Number.isFinite(anzahl) || anzahl <= 0) return `${gerundet} von 5`;
  const wort = anzahl === 1 ? "Bewertung" : "Bewertungen";
  return `${gerundet} von 5 bei ${anzahl.toLocaleString("de-DE")} ${wort}`;
}

/** Ergebnis-Status: die Quelle hat geantwortet (auch „nichts gefunden" ist eine Antwort). */
const HAT_GEANTWORTET = new Set(["found", "not_found", "ambiguous"]);

/**
 * Baut die Zeile zu EINER Quelle. Formuliert nichts: Zustand und Satz kommen fertig herein, die
 * Kopfzeile aus `standZeile()` (R1.6).
 */
function quellenStand(args: {
  quelle: FirmenprofilQuelle;
  zustand: AnreicherungsZustand;
  satz: string | null;
  zeile: AnreicherungsZeile | null | undefined;
  zeitzone?: string;
}): FirmenprofilQuellenStand {
  const zeile = args.zeile ?? null;
  const nachgesehen =
    zeile !== null && (HAT_GEANTWORTET.has(zeile.status) || standDatum(zeile.lookedUpAt, args.zeitzone) !== null);
  return {
    quelle: args.quelle,
    zustand: args.zustand,
    kopf: nachgesehen ? standZeile(args.quelle, zeile?.lookedUpAt, args.zeitzone) : args.quelle,
    satz: args.satz,
    nachgesehen,
  };
}

/* ────────────────────────────── Die Regel ──────────────────────────────────────────────────── */

/** Zustaende, in denen die gespeicherten Fakten weiterhin gezeigt werden (§ 4.1: „abgelaufen
 *  heisst nicht geloescht"; bei Mehrdeutigkeit SIND die Treffer die Information). */
const ZEIGT_FAKTEN: readonly AnreicherungsZustand[] = ["aktuell", "veraltet", "mehrdeutig"];

/**
 * Baut die Karte „Wer fragt hier an?" aus der Antwort von `GET /api/inquiries/:id/firmenprofil`.
 *
 * Reihenfolge der Entscheidungen (jede hat einen Grund):
 *  1. Nichts geladen oder keine Firma ⇒ die Karte entfaellt. Ohne Firmennamen stellt sich die
 *     Frage „Wer fragt hier an?" nicht — ein leerer Kasten waere schlechter als kein Kasten
 *     (R0.8 nennt diesen Fall serverseitig `keine_firma`).
 *  2. Zustand/Satz/Handlung kommen aus `@maestro/anreicherung`. Hier wird NICHTS formuliert.
 *  3. Die Firma ist IMMER die erste Angabe — mit Quelle „Anfrage" und dem Eingangsdatum als
 *     Stand. Sie ist die einzige Angabe, die es auch im Regelfall „nie gefragt" schon gibt;
 *     ohne sie stuende die Karte auf Produktion voellig leer da.
 *  4. Handelsregister-Fakten nur bei EINDEUTIGEM Treffer (`found`): bei `ambiguous` waere die
 *     Wahl eines der Treffer eine Erfindung — dort steht der Satz „N moegliche Treffer".
 *  5. Der letzte Bundesanzeiger-Eintrag mit `publishedAt` als Stand; fehlt es, sagt die
 *     Herkunftszeile „ohne Datum" (die Auswahl „der letzte" trifft bereits die Route, R0.8).
 */
export function firmenprofilKarte(lage: FirmenprofilKarteLage): FirmenprofilKarte {
  const { profil, anfrageEingang, jetzt, zeitzone } = lage;
  const modulName = lage.modulName ?? MODUL_HANDELSREGISTER;
  const firma = profil?.company?.trim() ?? "";

  // 1. Ohne Firma keine Karte.
  if (!profil || !firma) {
    return {
      sichtbar: false,
      titel: KARTEN_TITEL,
      firma: "",
      kurzinfo: "",
      zustand: "nie_gefragt",
      satz: null,
      handlung: null,
      angaben: [],
      quellen: [],
      googleWerte: [],
      bundesanzeigerSuchUrl: null,
    };
  }

  // 2. Zustand, Satz und Handlung — dieselbe Abbildung wie im Kunden-Profil und in der Route.
  const zeile = profil.handelsregister;
  const anzeige = anreicherungsAnzeige({
    modulAktiv: profil.modulAktiv,
    modulName,
    firma,
    jetzt,
    zeitzone,
    zeile: zeile
      ? {
          status: zeile.status,
          errorCode: zeile.errorCode,
          errorMessage: zeile.errorMessage,
          lookedUpAt: zeile.lookedUpAt,
          validUntil: zeile.validUntil,
          nextRetryAt: zeile.nextRetryAt,
          createdAt: zeile.createdAt,
          trefferAnzahl: Array.isArray(zeile.matches) ? zeile.matches.length : undefined,
          // Migration 245: ohne diese Zeile stuende ueber einer abgelehnten Zuordnung der Satz
          // „Als unpassend vermerkt" OHNE Datum — die Tatsache waere da, ihr Zeitpunkt nicht.
          verworfenAm: zeile.verworfenAm,
        }
      : null,
  });

  const angaben: FirmenprofilAngabe[] = [];

  // 3. Die Firma selbst — Quelle „Anfrage", Stand = Eingang der Anfrage.
  const firmenAngabe = angabe("Firma", firma, "Anfrage", anfrageEingang, zeitzone);
  if (firmenAngabe) angaben.push(firmenAngabe);

  const zeigtFakten = ZEIGT_FAKTEN.includes(anzeige.zustand);
  if (zeigtFakten && zeile) {
    // Der Stand der Registerangaben ist `looked_up_at` — wann tatsaechlich extern gefragt wurde.
    // BEWUSST kein Rueckfall auf `created_at`: das ist die Anlage der Zeile, nicht der Zeitpunkt
    // der Auskunft. Als Stand ausgegeben waere es eine falsche Behauptung; fehlt `looked_up_at`
    // (Bestandszeile), sagt die Herkunftszeile ehrlich „ohne Datum".
    const stand = zeile.lookedUpAt ?? null;
    const treffer = Array.isArray(zeile.matches) ? zeile.matches : [];
    // 4. Nur bei genau einem eindeutigen Ergebnis Fakten zeigen.
    const primaer = zeile.status === "found" ? (treffer[0] ?? null) : null;
    if (primaer) {
      const register = [primaer.registerCourt, primaer.registerNumber].filter((t) => typeof t === "string" && t.trim()).join(" · ");
      for (const kandidat of [
        angabe("Rechtsform", primaer.legalForm, "Handelsregister", stand, zeitzone),
        angabe("Register", register, "Handelsregister", stand, zeitzone),
        angabe("Sitz", primaer.seat, "Handelsregister", stand, zeitzone),
      ]) {
        if (kandidat) angaben.push(kandidat);
      }
    }

    // 5. Letzte Veroeffentlichung — Stand ist ihr eigenes Erscheinungsdatum, nicht der Abrufzeitpunkt.
    const letzte = profil.letzteVeroeffentlichung;
    const veroeffentlichung = letzte
      ? angabe("Letzte Veröffentlichung", letzte.title, "Bundesanzeiger", letzte.publishedAt, zeitzone)
      : null;
    if (veroeffentlichung) angaben.push(veroeffentlichung);
  }

  /* ── Stufe 1 ────────────────────────────────────────────────────────────────────────────────
   * Je Quelle EINE Zeile. Das Handelsregister immer (seine Lage steht oben schon fest), die drei
   * uebrigen nur, wenn die Antwort ueberhaupt etwas ueber sie sagt.
   */
  const quellen: FirmenprofilQuellenStand[] = [
    quellenStand({ quelle: "Handelsregister", zustand: anzeige.zustand, satz: anzeige.satz, zeile, zeitzone }),
  ];

  // 6. Google Places: Zustand — und, wenn ein Ergebnis vorliegt, die Bewertung als Angabe.
  //    BEWUSST ausserhalb des Handelsregister-Zweigs: die beiden Quellen sind unabhaengig, eine
  //    Firma ohne Registertreffer kann sehr wohl einen Google-Eintrag haben.
  let googleWerte: readonly unknown[] = [];
  const places = profil.places ?? null;
  if (places) {
    const placesAnzeige = anreicherungsAnzeige({
      modulAktiv: places.modulAktiv,
      modulName: MODUL_PLACES,
      firma,
      jetzt,
      zeitzone,
      zeile: places.zeile,
    });
    quellen.push(
      quellenStand({
        quelle: "Google Places",
        zustand: placesAnzeige.zustand,
        satz: placesAnzeige.satz,
        zeile: places.zeile,
        zeitzone,
      }),
    );
    if (places.zeile && ZEIGT_FAKTEN.includes(placesAnzeige.zustand)) {
      const text = bewertungsAngabe(places.zeile);
      const bewertung = angabe(LABEL_BEWERTUNG, text, "Google Places", places.zeile.lookedUpAt, zeitzone);
      if (bewertung) {
        angaben.push(bewertung);
        // Genau die Werte, die jetzt auf dem Schirm stehen — die Grundlage der Attribution (R4.5).
        googleWerte = [places.zeile.rating, places.zeile.userRatingsTotal];
      }
    }
  }

  // 7. Impressum und USt-IdNr.: NUR der Zustand. Beide Quellen haben eigene Vorbedingungen
  //    („keine Website hinterlegt", „nicht eingerichtet") — die formulieren ihre eigenen Regeln,
  //    hier wird nichts nachgebaut.
  const impressum = profil.impressum ?? null;
  if (impressum) {
    const k = impressumKarte({
      modulAktiv: impressum.modulAktiv,
      firma,
      website: impressum.website,
      zeile: impressum.zeile,
      jetzt,
      zeitzone,
    });
    quellen.push(quellenStand({ quelle: QUELLE_IMPRESSUM, zustand: k.zustand, satz: k.satz, zeile: impressum.zeile, zeitzone }));
  }

  const ustid = profil.ustid ?? null;
  if (ustid) {
    const k = ustIdKarte({
      modulAktiv: ustid.modulAktiv,
      firma,
      ustIdNr: ustid.ustIdNr,
      eigeneUstIdNr: ustid.eigeneUstIdNrHinterlegt ? EIGENE_USTID_VORHANDEN : null,
      zeile: ustid.zeile,
      jetzt,
      zeitzone,
    });
    quellen.push(quellenStand({ quelle: QUELLE_USTID, zustand: k.zustand, satz: k.satz, zeile: ustid.zeile, zeitzone }));
  }

  return {
    sichtbar: true,
    titel: KARTEN_TITEL,
    firma,
    kurzinfo: kurzinfoBauen(angaben, anzeige.satz),
    zustand: anzeige.zustand,
    satz: anzeige.satz,
    handlung: anzeige.handlung,
    angaben,
    quellen,
    googleWerte,
    bundesanzeigerSuchUrl: zeigtFakten ? (profil.bundesanzeigerSuchUrl ?? null) : null,
  };
}

/**
 * Was im eingeklappten Kopf steht.
 *
 * Ziel (P-1): eingeklappt muss sichtbar bleiben, OB es etwas gibt. Zwei Faelle, kein dritter:
 *  - Es gibt Recherche-Angaben ⇒ Zaehler + der aelteste Stand darunter. Der AELTESTE, nicht der
 *    neueste: eine Kurzinfo soll nicht frischer wirken als die schwaechste Angabe, die sie
 *    zusammenfasst.
 *  - Es gibt keine ⇒ der eine Satz zum Zustand („Noch nicht abgerufen.", „Zurzeit ausgesetzt …").
 *    Damit entsteht hier KEINE zweite Formulierungstabelle neben `@maestro/anreicherung`.
 *
 * Der Notnagel „Keine Angaben" greift nur, wenn beides fehlt (Status `found` ohne Treffer) — ein
 * leerer Kopf waere genau die stille Leerstelle, die dieser Loop abschafft.
 */
function kurzinfoBauen(angaben: FirmenprofilAngabe[], satz: string | null): string {
  const recherche = angaben.filter((a) => a.quelle !== "Anfrage");
  if (recherche.length > 0) {
    const staende = recherche.map((a) => a.stand).filter((s): s is string => s !== null);
    const zaehler = `${recherche.length} ${recherche.length === 1 ? "Angabe" : "Angaben"}`;
    if (staende.length < recherche.length) return `${zaehler} · Stand teilweise unbekannt`;
    return `${zaehler} · Stand ${aeltesterStand(staende)}`;
  }
  return satz ?? "Keine Angaben";
}

/** Das aelteste der bereits formatierten „TT.MM.JJJJ"-Daten. */
function aeltesterStand(staende: string[]): string {
  return staende.reduce((a, b) => (sortierbar(a) <= sortierbar(b) ? a : b));
}

/** „12.06.2026" -> „20260612" (nur zum Vergleichen, nie zum Anzeigen). */
function sortierbar(deutsch: string): string {
  const [tag, monat, jahr] = deutsch.split(".");
  return `${jahr ?? ""}${monat ?? ""}${tag ?? ""}`;
}

/* ══════════ R1.7 — die Karte „Firmenprofil" im Kunden-Profil ═════════════════════════════════
 *
 * Zwei Regeln, die bis hierher in `apps/web/src/pages/customer-detail.tsx` als JSX standen bzw.
 * gar nicht existierten. Beide gehoeren hierher, weil sie AUSSAGEN sind und keine Formatierung —
 * dieselbe Ueberlegung wie bei „gesucht vs. nicht nachgesehen" (Stufe 1) und bei `OHNE_DATUM`
 * (R1.6): was passiert, wenn etwas fehlt, muss gepruefte Regel sein und darf nicht davon
 * abhaengen, welche Komponente gerade rendert.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * Was an die Stelle eines Registerfeldes tritt, zu dem die Quelle NICHTS geliefert hat.
 *
 * Bis R1.7 fiel so ein Feld ersatzlos aus der Liste — die Karte zeigte dann vier Zeilen statt
 * fuenf, und niemand konnte sehen, ob zu dieser Firma kein Sitz vorliegt oder ob wir ihn nicht
 * gelesen haben. Das ist derselbe Fehler wie „gesucht und nichts gefunden" vs. „nicht
 * nachgesehen", nur eine Ebene tiefer.
 *
 * Der Wortlaut ist bewusst eine Aussage ueber die LIEFERUNG und nicht ueber das Register: Wir
 * wissen, dass in unserem Ergebnis nichts steht. Ob der Registereintrag selbst das Feld fuehrt,
 * wissen wir nicht — „im Handelsregister nicht eingetragen" waere genau die Behauptung, die
 * dieser Loop abschafft.
 */
export const OHNE_WERT = "vom Handelsregister nicht geliefert";

/** Ein Feld der Registerangaben: Name, Wert (oder `null` = nicht geliefert). */
export interface RegisterFeld {
  label: string;
  /** `null` heisst: die Quelle hat zu diesem Feld nichts geliefert. Anzeige dann `OHNE_WERT`. */
  wert: string | null;
}

/**
 * Die fuenf Registerangaben in fester Reihenfolge — IMMER alle fuenf, auch die leeren.
 *
 * Feste Liste mit Absicht: eine Liste, die nur zeigt, was da ist, kann nicht zwischen „nichts
 * geliefert" und „gibt es bei uns nicht" unterscheiden. Die Reihenfolge ist die des
 * Registerauszugs (welche Form, wo eingetragen, wo ansaessig, wer vertritt, seit wann).
 *
 * `lastEntryDate` ist das Datum des REGISTEREINTRAGS und NICHT der Stand unserer Auskunft — der
 * steht in der Herkunftszeile des ganzen Blocks (`standZeile("Handelsregister", lookedUpAt)`).
 * Genau diese Verwechslung war in R1.6 schon einmal Thema; deshalb formatiert sie hier
 * `standDatum` und traegt trotzdem ihr eigenes Label.
 */
export function registerFelder(treffer: FirmenprofilTreffer | null | undefined, zeitzone?: string): RegisterFeld[] {
  const text = (w: string | null | undefined): string | null => {
    const t = typeof w === "string" ? w.trim() : "";
    return t ? t : null;
  };
  const vertreter = (treffer?.representatives ?? []).map((n) => (typeof n === "string" ? n.trim() : "")).filter(Boolean);
  return [
    { label: "Rechtsform", wert: text(treffer?.legalForm) },
    {
      label: "Register",
      wert: text([treffer?.registerCourt, treffer?.registerNumber].filter((t) => typeof t === "string" && t.trim()).join(" · ")),
    },
    { label: "Sitz", wert: text(treffer?.seat) },
    { label: "Geschäftsführer", wert: vertreter.length > 0 ? vertreter.join(", ") : null },
    { label: "Letzte Eintragung", wert: standDatum(treffer?.lastEntryDate, zeitzone) },
  ];
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Die Trefferauswahl im Handelsregister (Migration 245)
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Die Lage, aus der sich ergibt, ob eine Registerauswahl offensteht — Spiegelbild von
 *  `TrefferauswahlLage` in `@maestro/places-anzeige`. */
export interface RegisterTrefferauswahlLage {
  /** Lebenszyklus-Status der Zeile (Migration 221). Nur `'ambiguous'` öffnet eine Auswahl. */
  status?: string | null;
  /** Die gespeicherten Treffer (`matches`) — `null`/leer heißt: es steht keine Auswahl offen. */
  treffer?: readonly FirmenprofilTreffer[] | null;
  /** Wann das Register gefragt wurde. Der Stand der ganzen Liste — sie stammt aus EINEM Abruf. */
  lookedUpAt?: string | null;
  /** IANA-Zone. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
}

/**
 * Die Registerauswahl bei Mehrdeutigkeit — oder `null`, wenn keine offensteht.
 *
 * ── Warum es sie überhaupt gibt ──────────────────────────────────────────────────────────────
 * `status='ambiguous'` gibt es im Handelsregister seit sql/92, und bis Migration 245 trug dieser
 * Zustand in der Oberfläche eine ZAHL („3 mögliche Treffer — bitte auswählen.") und keine einzige
 * Handlung. Der Betrieb sah, dass es mehrere gibt, und konnte nichts tun; die Zeile blieb liegen,
 * die Karte zeigte keine Registerangaben (die wären ja die eines von drei Betrieben), und der
 * nächste Anlass fragte dieselbe Firma erneut ab. Auf Produktion sind 88 Zeilen in diesem
 * Zustand (Messung R0.3) — das ist kein Randfall.
 *
 * ── Sie folgt der Google-Auswahl, sie erfindet nichts Zweites ────────────────────────────────
 * Gleiche Ausgabeform (`Trefferliste` aus `@maestro/anreicherung`), gleicher Satzbau
 * (`trefferauswahlSatz`), gleiche Strenge: eine Auswahl entsteht NUR bei `'ambiguous'` UND
 * mindestens zwei Treffern. Ein `'ambiguous'` mit einem einzigen Treffer ist ein Widerspruch in
 * den Daten; daraus eine Auswahl mit einem Knopf zu bauen hieße, ihn zu verstecken.
 *
 * ── Der Unterschied zu Google, und warum er sein muss ────────────────────────────────────────
 * Google liefert eine `place_id` — einen Schlüssel, den die Auswahl zurückgeben kann. Die
 * kostenlose Registersuche liefert keinen: aus der Ergebnisliste sind `representatives` und
 * `lastEntryDate` gar nicht verfügbar, und auch die Registernummer kann fehlen (siehe
 * `apps/api/src/lib/handelsregister.ts`). Ein Schlüssel, der `null` sein darf, ist keiner.
 * Deshalb ist `id` hier der INDEX in `matches` — exakt, weil die API-Auswahl dieselbe Version
 * bestätigt (Zeilen-Id + `looked_up_at`) und damit genau diese Liste festnagelt.
 *
 * `warnung` bleibt immer `null`: das Register kennt kein Gegenstück zu „dauerhaft geschlossen".
 * Ein erfundener Hinweis wäre schlimmer als keiner.
 */
export function registerTrefferauswahl(lage: RegisterTrefferauswahlLage): Trefferliste | null {
  if (lage.status !== "ambiguous") return null;
  const treffer = (lage.treffer ?? []).filter((t): t is FirmenprofilTreffer => Boolean(t));
  if (treffer.length < 2) return null;

  const eintraege = treffer.map<TrefferEintrag>((t, index) => {
    const text = (w: string | null | undefined): string | null => {
      const s = typeof w === "string" ? w.trim() : "";
      return s ? s : null;
    };
    // Genau die Angaben, an denen sich zwei gleichnamige Einträge unterscheiden lassen — in
    // derselben Reihenfolge wie in `registerFelder()`, damit die Auswahl und die Karte danach
    // dasselbe Bild ergeben. Register und Gericht bilden EINE Zeile („Amtsgericht … · HRB …"),
    // wie in der Feldliste auch.
    const zeilen = [
      text(t.legalForm),
      text([t.registerCourt, t.registerNumber].map((s) => (typeof s === "string" ? s.trim() : "")).filter(Boolean).join(" · ")),
      text(t.seat),
    ].filter((z): z is string => Boolean(z));
    return {
      id: String(index),
      // Ohne Namen steht dort der Sachverhalt, nicht nichts (dieselbe Regel wie bei Google).
      titel: text(t.companyName) ?? "Ohne Namen im Register",
      zeilen,
      warnung: null,
    };
  });

  return Object.freeze({
    satz: trefferauswahlSatz(eintraege.length, "Eintrag"),
    herkunft: standZeile("Handelsregister", lage.lookedUpAt ?? null, lage.zeitzone),
    eintraege,
  });
}

/**
 * Ein bei einer Auskunftei GEKAUFTER Bonitaetswert.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ANZEIGEN JA — SELBST ERZEUGEN NEIN — ENTSCHEIDEN NIE (Antoines stehende Regel)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * Zweck, woertlich von Antoine (07.08.2026): *„Der Tennant soll eine Einschätzung bekommen, ob er
 * den Gast auf Rechnung abrechnen lässt oder nur Vorkasse/Zahlung bei Event."* Daraus folgen drei
 * Grenzen, und alle drei sind hier eingebaut, nicht nur aufgeschrieben:
 *
 *  1. **Selbst erzeugen: nie.** `wert` ist eine Zeichenkette, die der ANBIETER geliefert hat.
 *     Dieser Baustein rechnet nichts, vergleicht nichts, staffelt nichts — es gibt keine Zahl und
 *     keine Schwelle, an der eine eigene Einordnung entstehen koennte. § 31 BDSG verlangt fuer
 *     einen Wahrscheinlichkeitswert ein wissenschaftlich anerkanntes mathematisch-statistisches
 *     Verfahren; MAESTRO hat keins und soll keins bauen.
 *  2. **Nur fuer den Mandanten.** Das Ergebnis ist eine `FirmenprofilAngabe` fuer die
 *     Backoffice-Karte. Es gibt bewusst KEINEN Weg von hier in `lib/offer-briefing.ts` oder auf
 *     eine Gast-Flaeche.
 *  3. **Loest nie etwas aus.** Rueckgabe ist eine Angabe — kein Zustand, kein Flag, keine
 *     Zahlungsbedingung, kein Preis. Ein Score, der eine Kondition vorbelegt, waere eine
 *     automatisierte Einzelentscheidung (Art. 22 DSGVO, EuGH C-634/21).
 *
 * `null` heisst: es liegt keiner vor — dann steht dort NICHTS. Ausdruecklich auch dann `null`,
 * wenn Anbieter oder Wert fehlen: ein Score ohne benannte Quelle ist keine Angabe, sondern eine
 * Behauptung. Ohne Datum erscheint er trotzdem, dann sagt die Herkunftszeile „ohne Datum"
 * (R1.6) — verschwiegen wird nichts, erfunden auch nicht.
 *
 * Heute liefert diese Funktion in der laufenden Software immer `null`: es gibt keine
 * Auskunftei-Anbindung. Sie steht trotzdem hier, damit der Platz OFFENSICHTLICH ist — wer eine
 * anschliesst, findet die drei Grenzen an der Stelle, an der er arbeitet, statt sie in einem
 * Dokument suchen zu muessen.
 */
export interface GekaufterScore {
  /** Die Auskunftei, die den Wert geliefert hat („Creditreform", „boniforce" …). Pflicht. */
  anbieter: string;
  /** Der Wert GENAU SO, wie der Anbieter ihn ausweist („312 von 600", „Klasse B"). Pflicht. */
  wert: string;
  /** Wann der Anbieter den Wert ermittelt/geliefert hat (ISO). Fehlt er: „ohne Datum". */
  standIso?: string | null;
}

export function gekaufterScoreAngabe(
  score: GekaufterScore | null | undefined,
  zeitzone?: string,
): FirmenprofilAngabe | null {
  const anbieter = (score?.anbieter ?? "").trim();
  const wert = (score?.wert ?? "").trim();
  if (!anbieter || !wert) return null;
  const standIso = score?.standIso ?? null;
  return {
    // Der Anbieter steht IM Label, weil „Bonitätsscore" allein nicht sagt, wessen Score es ist —
    // und weil es keinen MAESTRO-Score gibt, mit dem man ihn verwechseln koennte.
    label: `Bonitätsscore (${anbieter})`,
    wert,
    quelle: "Auskunftei",
    stand: standDatum(standIso, zeitzone),
    herkunft: standZeile("Auskunftei", standIso, zeitzone),
  };
}
